/**
 * Formats a Polish address from granular components
 * @param {Object} addressData - Object containing address components
 * @param {string} addressData.street - Street name
 * @param {string} addressData.buildingNumber - Building number
 * @param {string} addressData.postalCode - Postal code
 * @param {string} addressData.city - City name
 * @returns {string} Formatted address string
 */
export const formatAddress = ({ street, buildingNumber, postalCode, city }) => {
  const parts = [];
  
  if (street) {
    // Combine street with building number if available
    const streetWithNumber = buildingNumber ? `${street} ${buildingNumber}` : street;
    parts.push(streetWithNumber);
  }
  if (postalCode && city) {
    parts.push(`${postalCode} ${city}`);
  } else if (city) {
    parts.push(city);
  }
  
  return parts.join(', ');
};

/**
 * Formats investment location address
 * @param {Object} investment - Investment object with location fields
 * @returns {string} Formatted investment address
 */
export const formatInvestmentAddress = (investment) => {
  if (!investment) return '';
  
  // Try to use granular fields first
  if (investment.location_street || investment.location_city) {
    return formatAddress({
      street: investment.location_street,
      buildingNumber: investment.location_building_number,
      postalCode: investment.location_postal_code,
      city: investment.location_city
    });
  }
  
  // Fallback to investment_address if granular fields are not available
  return investment.investment_address || '';
};

/**
 * Formats tenant/investor headquarters address
 * @param {Object} tenant - Tenant object with HQ fields
 * @returns {string} Formatted HQ address
 */
export const formatTenantHQAddress = (tenant) => {
  if (!tenant) return '';
  
  // Try to use granular fields first
  if (tenant.hq_street || tenant.hq_city) {
    return formatAddress({
      street: tenant.hq_street,
      buildingNumber: tenant.hq_building_number,
      postalCode: tenant.hq_postal_code,
      city: tenant.hq_city
    });
  }
  
  // Fallback to investor_address if granular fields are not available
  return tenant.investor_address || '';
};

/**
 * Formats investment sales office address
 * @param {Object} investment - Investment object with sales office fields
 * @returns {string} Formatted sales office address
 */
export const formatSalesOfficeAddress = (investment) => {
  if (!investment) return '';
  
  // Check if sales office fields exist
  if (investment.sales_street || investment.sales_city) {
    return formatAddress({
      street: investment.sales_street,
      buildingNumber: investment.sales_building_number,
      postalCode: investment.sales_postal_code,
      city: investment.sales_city
    });
  }
  
  // Fallback to investment location if no separate sales office
  return formatInvestmentAddress(investment);
};

/**
 * Formats investment HQ address (from investment's HQ fields)
 * @param {Object} investment - Investment object with HQ fields
 * @returns {string} Formatted HQ address
 */
export const formatInvestmentHQAddress = (investment) => {
  if (!investment) return '';
  
  // Try to use HQ fields from investment
  if (investment.hq_street || investment.hq_city) {
    return formatAddress({
      street: investment.hq_street,
      buildingNumber: investment.hq_building_number,
      postalCode: investment.hq_postal_code,
      city: investment.hq_city
    });
  }
  
  return '';
};